import {
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  SaveOutlined,
} from '@ant-design/icons';
import {
  Drawer,
  Button,
  message,
  Spin,
  Row,
  Col,
  Typography,
  ModalProps,
  Modal,
  notification,
} from 'antd';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import PageTitle from '../../components/PageTitle';
import StaffUserForm, { StaffUserFormValues } from '../../components/StaffUserForm';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import styles from './EditUser.module.css';

interface ConfirmSoftDeleteModalProps extends ModalProps {
  userFullName: string;
  onSuccess?: () => void | Promise<unknown>;
}

const ConfirmSoftDeleteModal = ({ userFullName, ...props }: ConfirmSoftDeleteModalProps) => {
  return (
    <Modal
      okButtonProps={{
        htmlType: 'button',
      }}
      okText="Sim, deletar usuário"
      title={
        <Typography.Text strong>
          <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 20 }} /> Atenção!
        </Typography.Text>
      }
      {...props}
    >
      <Typography.Text>
        Ao confirmar, o usuário <Typography.Text strong>{userFullName}</Typography.Text> será
        excluído permanentemente e não poderá ser recuperado. Tem certeza que deseja continuar?
      </Typography.Text>
    </Modal>
  );
};

const EditUser = () => {
  const [form] = Form.useForm<StaffUserFormValues>();
  const { tenant, user_id } = useTypedParams({ tenant: String, user_id: Number });
  const queryClient = useQueryClient();

  const [confirmUserSoftDeleteIsOpen, setConfirmUserSoftDeleteIsOpen] = useState<boolean>(false);

  const [searchParams] = useSearchParams();

  const user = useAPIQuery(`/staff-users/${user_id}/`);

  const navigate = useTypedNavigate();

  const [setIsDirty] = useDiscardChangesModal(form);

  useEffect(() => {
    if (user.isFetched && !user.data?.is_active) {
      navigate({ pathname: '/:tenant/settings/cs-staff/staff-users/', params: { tenant } });
    }
  }, [navigate, tenant, user]);

  const mutation = useAPIMutation(
    (variables: StaffUserFormValues, api) => {
      return api.patch(`/staff-users/${user_id}/`, {
        full_name: variables.name,
        email: variables.email,
        username: variables.username,
      });
    },
    {
      onSuccess: () => {
        setIsDirty(false);
        message.success('Usuário atualizado!');
        navigate({ pathname: '/:tenant/settings/cs-staff/staff-users/', params: { tenant } });
      },
      onError: (err, variables, context) => {
        if (err?.response?.data?.email?.[0] === 'usuário com este Endereço de e-mail já existe.') {
          return notification.error({
            duration: 0,
            message: 'Atenção',
            description: 'Já existe um usuário com este endereço de E-mail cadastrado.',
          });
        }

        if (err?.response?.data?.username?.[0]) {
          return notification.error({
            duration: 0,
            message: 'Atenção',
            description: err.response.data.username[0],
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
      onSettled: () => {
        queryClient.invalidateQueries('/staff-users/');
        queryClient.invalidateQueries(`/staff-users/${user_id}/`);
      },
    }
  );

  const softDeleteUserMutation = useAPIMutation(
    (_, api) =>
      api.patch(`/staff-users/${user_id}/`, {
        is_active: false,
      }),
    {
      onSuccess: () => {
        message.success('Usuário excluído com sucesso!');
        navigate({ pathname: '/:tenant/settings/cs-staff/staff-users/', params: { tenant } });
      },
      onSettled: () => {
        queryClient.invalidateQueries('/staff-users/');
      },
    }
  );

  return (
    <>
      <ConfirmSoftDeleteModal
        key={`ConfirmSoftDeleteModal-${user_id}`}
        open={confirmUserSoftDeleteIsOpen}
        onOk={() => {
          setConfirmUserSoftDeleteIsOpen(false);
          softDeleteUserMutation.mutate({});
        }}
        onCancel={() => setConfirmUserSoftDeleteIsOpen(false)}
        onSuccess={() => {
          setConfirmUserSoftDeleteIsOpen(false);
        }}
        destroyOnClose
        userFullName={user.data?.full_name || '...'}
      />
      <Drawer
        className={styles.drawer}
        title={
          <PageTitle
            title={user.data?.username || '...'}
            level={2}
            extra={[
              <Button
                key="close"
                type="text"
                onClick={() =>
                  navigate({
                    pathname: '/:tenant/settings/cs-staff/staff-users/',
                    params: { tenant },
                  })
                }
                shape="circle"
              >
                <CloseOutlined />
              </Button>,
            ]}
          />
        }
        maskClosable
        size="large"
        closable={false}
        width={720}
        footer={
          <Row gutter={[16, 16]}>
            <Col>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={mutation.isLoading || softDeleteUserMutation.isLoading}
                onClick={() => form?.submit()}
              >
                Salvar
              </Button>
            </Col>
            <Col>
              <Button
                loading={mutation.isLoading || softDeleteUserMutation.isLoading}
                onClick={() =>
                  navigate({
                    pathname: '/:tenant/settings/cs-staff/staff-users/',
                    params: { tenant },
                    search: searchParams,
                  })
                }
              >
                Fechar
              </Button>
            </Col>
            <Col flex={1} />
            <Col>
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={mutation.isLoading || softDeleteUserMutation.isLoading}
                onClick={() => setConfirmUserSoftDeleteIsOpen(true)}
              >
                Deletar
              </Button>
            </Col>
          </Row>
        }
        open
        onClose={() =>
          navigate({ pathname: '/:tenant/settings/cs-staff/staff-users/', params: { tenant } })
        }
      >
        <Helmet>
          <title>
            Editar | Perfil staff {user.data?.username || '...'} | Recrutamento e Seleção
          </title>
        </Helmet>
        <ErrorResult error={user.error}>
          <Spin spinning={user.isFetching}>
            <StaffUserForm
              form={form}
              initialValues={{
                name: user.data?.full_name || '',
                username: user.data?.username,
                email: user.data?.email,
              }}
              layout="vertical"
              onValuesChange={() => setIsDirty(form.isFieldsTouched())}
              onFinish={(variables) => {
                mutation.mutate(variables);
              }}
            />
          </Spin>
        </ErrorResult>
      </Drawer>
    </>
  );
};

export default withRouteChange()(EditUser);
